import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ChevronRight, MapPin, Phone, MessageCircle, Mail,
  Clock, Instagram, Facebook, Youtube, CheckCircle, Send, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.19a8.18 8.18 0 0 0 4.78 1.52V6.27a4.85 4.85 0 0 1-1.01-.42z" />
  </svg>
);

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const inputClass = `w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700
  bg-white dark:bg-[#121020] text-[#1A1A2E] dark:text-gray-100
  placeholder-gray-400 dark:placeholder-gray-600 text-sm
  focus:border-[#7B2FBE] dark:focus:border-[#9333EA] focus:ring-0 transition-colors`;

const Kontak = () => {
  const { isDark } = useTheme();
  const [form, setForm] = useState({ nama: '', email: '', wa: '', keperluan: '', pesan: '', _gotcha: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);

  const validate = () => {
    const errs = {};
    if (!form.nama.trim()) errs.nama = 'Nama lengkap wajib diisi';
    if (!form.email.trim()) errs.email = 'Email wajib diisi';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Format email tidak valid';
    if (!form.wa.trim()) errs.wa = 'Nomor WhatsApp wajib diisi';
    else if (!/^[0-9+\s-]{9,15}$/.test(form.wa)) errs.wa = 'Format nomor tidak valid';
    if (!form.keperluan) errs.keperluan = 'Pilih keperluan Anda';
    if (!form.pesan.trim()) errs.pesan = 'Pesan wajib diisi';
    else if (form.pesan.trim().length < 10) errs.pesan = 'Pesan minimal 10 karakter';
    else if (form.pesan.trim().length > 1000) errs.pesan = 'Pesan maksimal 1000 karakter';
    
    if (!turnstileToken) errs.turnstile = 'Harap selesaikan verifikasi keamanan';
    
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // 1. Honeypot check
    if (form._gotcha) {
      console.warn('Bot detected via honeypot');
      return;
    }

    // 2. Client-side Rate Limiting (60 seconds)
    const lastSubmit = localStorage.getItem('last_kontak_submit');
    const now = Date.now();
    if (lastSubmit && now - lastSubmit < 60000) {
      const waitTime = Math.ceil((60000 - (now - lastSubmit)) / 1000);
      setSubmitError(`Harap tunggu ${waitTime} detik sebelum mengirim pesan lagi.`);
      return;
    }

    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    
    try {
      const response = await fetch('/api/kontak', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...form,
          token: turnstileToken
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        localStorage.setItem('last_kontak_submit', Date.now().toString());
      } else {
        setSubmitError(result.error || 'Gagal mengirim pesan. Silakan coba lagi.');
      }
    } catch (err) {
      setSubmitError('Terjadi kesalahan koneksi. Silakan periksa internet Anda.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const contactCards = [
    { icon: <MapPin className="text-[#7B2FBE]" size={22} />, title: 'Alamat', content: 'Jl. Tarmizi Kadir No.71\nPakuan Baru 36132, Jambi' },
    { icon: <Phone className="text-[#7B2FBE]" size={22} />, title: 'Telepon', content: '(0741) 7552270\n(0741) 7552710' },
    {
      icon: <MessageCircle className="text-[#16A34A]" size={22} />, title: 'WhatsApp', content: '082177711609',
      action: { href: 'https://wa.me/6282177711609', label: 'Chat Sekarang', color: 'bg-[#25D366]' }
    },
    {
      icon: <Mail className="text-[#7B2FBE]" size={22} />, title: 'Email', content: 'surat@stikes-hi.ac.id',
      action: { href: 'mailto:surat@stikes-hi.ac.id', label: 'Kirim Email', color: 'bg-[#7B2FBE]' }
    },
    {
      icon: <Clock className="text-[#7B2FBE]" size={22} />, title: 'Jam Operasional',
      content: 'Senin–Jumat: 08.00–16.00 WIB\nSabtu: 08.00–12.00 WIB'
    },
  ];

  const socials = [
    { icon: <Instagram size={18} />, href: 'https://www.instagram.com/stikesharapanibu/ ', label: 'Instagram' },
    { icon: <Facebook size={18} />, href: 'https://facebook.com/stikeshiofficial', label: 'Facebook' },
    { icon: <Youtube size={18} />, href: 'https://youtube.com/@stikeshijambi', label: 'YouTube' },
    { icon: <TikTokIcon />, href: 'https://tiktok.com/@stikeshijambi', label: 'TikTok' },
  ];

  return (
    <div data-testid="kontak-page">
      <SEO
        title="Hubungi Kami"
        description="Hubungi STIKES Harapan Ibu Jambi - Dapatkan informasi pendaftaran, akademik, dan layanan kampus. Alamat, telepon, email, dan media sosial kami tersedia di sini."
      />
      <PageHero title="Hubungi Kami" breadcrumbs={['Kontak']} />

      <section className="py-16 bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* LEFT: Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white mb-8 font-heading">
                Informasi Kontak
              </h2>
              <div className="space-y-4">
                {contactCards.map((card, i) => (
                  <div
                    key={i}
                    data-testid={`contact-card-${i}`}
                    className="p-5 rounded-2xl bg-white dark:bg-[#1A182E] border border-gray-100 dark:border-gray-800 shadow-sm"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
                        {card.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-[#1A1A2E] dark:text-white text-sm mb-1 font-heading">{card.title}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line">{card.content}</div>
                        {card.action && (
                          <a
                            href={card.action.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`contact-action-${i}`}
                            className={`inline-block mt-3 ${card.action.color} text-white text-xs font-semibold px-4 py-2 rounded-full hover:opacity-90 transition-all`}
                          >
                            {card.action.label}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Media */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">Media Sosial</p>
                <div className="flex gap-3">
                  {socials.map(({ icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${label.toLowerCase()}`}
                      aria-label={label}
                      className="w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center
                        text-gray-500 dark:text-gray-400 hover:bg-[#7B2FBE] hover:border-[#7B2FBE] hover:text-white
                        transition-all duration-300 hover:scale-110"
                    >
                      {icon}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* RIGHT: Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-white dark:bg-[#1A182E] rounded-3xl p-8 shadow-sm border border-gray-100 dark:border-gray-800">
                <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white mb-8 font-heading">Kirim Pesan</h2>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="form-success"
                    className="text-center py-12"
                  >
                    <div className="w-20 h-20 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="text-[#16A34A]" size={40} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1A1A2E] dark:text-white mb-2 font-heading">Pesan Terkirim!</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm max-w-sm mx-auto">
                      Terima kasih! Pesan Anda telah kami terima dan notifikasi telah dikirim ke tim admin kami. Kami akan segera menghubungi Anda.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ nama: '', email: '', wa: '', keperluan: '', pesan: '', _gotcha: '' }); setTurnstileToken(null); }}
                      className="mt-8 flex items-center gap-2 mx-auto bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-all"
                    >
                      <RefreshCw size={14} /> Kirim Pesan Lain
                    </button>
                  </motion.div>
                ) : (
                  <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot field for bot protection */}
                    <input 
                      type="text" 
                      name="_gotcha" 
                      value={form._gotcha} 
                      onChange={handleChange} 
                      style={{ display: 'none' }} 
                      tabIndex="-1" 
                      autoComplete="off" 
                    />

                    <AnimatePresence>
                      {submitError && (
                        <motion.div 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-2xl flex items-start gap-3"
                        >
                          <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={18} />
                          <p className="text-sm text-red-600 dark:text-red-400 font-medium">{submitError}</p>
                        </motion.div>
                       )}
                    </AnimatePresence>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                          Nama Lengkap <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="nama"
                          maxLength={100}
                          value={form.nama}
                          onChange={handleChange}
                          placeholder="Masukkan nama Anda"
                          className={inputClass}
                        />
                        {errors.nama && <p className="text-red-500 text-[10px] font-bold mt-1.5 uppercase px-1">{errors.nama}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                          WhatsApp <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="wa"
                          maxLength={15}
                          value={form.wa}
                          onChange={handleChange}
                          placeholder="0812xxxxxxxx"
                          className={inputClass}
                        />
                        {errors.wa && <p className="text-red-500 text-[10px] font-bold mt-1.5 uppercase px-1">{errors.wa}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                        Email Aktif <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        maxLength={150}
                        value={form.email}
                        onChange={handleChange}
                        placeholder="contoh@email.com"
                        className={inputClass}
                      />
                      {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1.5 uppercase px-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                        Keperluan <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="keperluan"
                        value={form.keperluan}
                        onChange={handleChange}
                        className={inputClass}
                      >
                        <option value="">-- Pilih Keperluan --</option>
                        <option value="pmb">Informasi PMB</option>
                        <option value="akademik">Informasi Akademik</option>
                        <option value="kerjasama">Kerjasama Institusi</option>
                        <option value="pengaduan">Pengaduan</option>
                        <option value="lainnya">Lainnya</option>
                      </select>
                      {errors.keperluan && <p className="text-red-500 text-[10px] font-bold mt-1.5 uppercase px-1">{errors.keperluan}</p>}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2 px-1">
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">
                          Pesan Pertanyaan <span className="text-red-500">*</span>
                        </label>
                        <span className={`text-[10px] font-bold ${form.pesan.length > 900 ? 'text-orange-500' : 'text-gray-400'}`}>
                          {form.pesan.length} / 1000
                        </span>
                      </div>
                      <textarea
                        name="pesan"
                        value={form.pesan}
                        onChange={handleChange}
                        maxLength={1000}
                        rows={5}
                        placeholder="Tuliskan pesan atau pertanyaan Anda di sini..."
                        className={`${inputClass} resize-none`}
                      />
                      {errors.pesan && <p className="text-red-500 text-[10px] font-bold mt-1.5 uppercase px-1">{errors.pesan}</p>}
                    </div>

                    <div className="py-2 flex justify-center lg:justify-start">
                      <Turnstile
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                        theme="auto"
                        onSuccess={(token) => setTurnstileToken(token)}
                        onError={() => setSubmitError('Gagal memuat verifikasi keamanan. Harap muat ulang halaman.')}
                        onExpire={() => setTurnstileToken(null)}
                      />
                    </div>
                    {errors.turnstile && <p className="text-red-500 text-[10px] font-bold uppercase px-1">{errors.turnstile}</p>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA]
                        text-white py-4 rounded-2xl font-bold text-sm hover:opacity-90
                        transition-all duration-300 hover:scale-[1.01] active:scale-[0.98]
                        disabled:opacity-70 disabled:cursor-not-allowed shadow-xl shadow-purple-500/20"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="animate-spin" size={18} />
                          MENGIRIM PESAN...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          KIRIM SEKARANG
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PMB Call to Action */}
      <section className="py-16 relative overflow-hidden" data-testid="pmb-cta-section"
        style={{ background: 'linear-gradient(135deg, #4C1D95 0%, #7B2FBE 50%, #6D28D9 100%)' }}>
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-white/5 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-white/5 blur-3xl pointer-events-none" />
        {/* Dots pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-1.5 text-white text-xs font-semibold mb-5 uppercase tracking-widest">
              Penerimaan Mahasiswa Baru
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-heading mb-4 leading-tight">
              Bergabunglah Bersama Kami di<br className="hidden sm:block" /> STIKES Harapan Ibu Jambi
            </h2>
            <p className="text-white/75 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Pendaftaran Mahasiswa Baru Tahun Akademik 2026/2027 kini telah dibuka.
              Raih karir terbaik di bidang kesehatan bersama kami.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://pmb.stikes-hi.ac.id"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pmb-cta-btn"
                className="inline-flex items-center gap-2.5 bg-white text-[#7B2FBE] px-8 py-3.5 rounded-full font-bold text-sm
                  hover:bg-purple-50 transition-all duration-300 hover:scale-105 active:scale-95 shadow-xl shadow-black/20"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
                Daftar Sekarang — PMB Online
              </a>
              <a
                href="https://wa.me/6282177711609?text=Halo%2C%20saya%20ingin%20informasi%20PMB%20STIKES%20HI%20Jambi"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pmb-wa-btn"
                className="inline-flex items-center gap-2.5 border-2 border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-sm
                  hover:bg-white/10 transition-all duration-300 hover:scale-105 active:scale-95 backdrop-blur-sm"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
                </svg>
                Tanya via WhatsApp
              </a>
            </div>
            <p className="text-white/50 text-xs mt-6">
              Kunjungi <span className="font-semibold text-white/80">pmb.stikes-hi.ac.id</span> untuk informasi lengkap syarat & jadwal pendaftaran
            </p>
          </motion.div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="bg-white dark:bg-[#100E1C]" data-testid="maps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white mb-8 text-center font-heading">
            Lokasi Kampus
          </h2>
          <div className="rounded-3xl overflow-hidden shadow-xl border border-gray-100 dark:border-gray-800" style={{ height: '420px' }}>
            <iframe
              title="Lokasi STIKES Harapan Ibu Jambi"
              src="https://maps.google.com/maps?q=STIKES+Harapan+Ibu+Jambi+Jl.+Tarmizi+Kadir&t=&z=16&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="maps-iframe"
            />
          </div>
          <p className="text-center text-sm text-gray-400 mt-4">
            Jl. Tarmizi Kadir No.71, Pakuan Baru 36132, Jambi
          </p>
        </div>
      </section>
    </div>
  );
};

export default Kontak;
